import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { DSSetup, PopupListItem, Service, ValidationError } from 'webapp-types';
import { SeriesVisualization, Theme, Unit } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { copyObject, isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockSettingsFiltersComponent from 'webapp/components/block/settings/filters';
import BlockSettingsMetricAdditionalLinesComponent from 'webapp/components/block/settings/metric/additional-lines';
import BlockSettingsMetricAggregationComponent from 'webapp/components/block/settings/metric/aggregation';
import BlockSettingsMetricComparesComponent from 'webapp/components/block/settings/metric/compares';
import BlockSettingsMetricGaugeGoalComponent from 'webapp/components/block/settings/metric/gauge-goal';
import BlockSettingsMetricMaxValueComponent from 'webapp/components/block/settings/metric/max-value';
import BlockSettingsMetricMinValueComponent from 'webapp/components/block/settings/metric/min-value';
import BlockSettingsMetricRecordLimitComponent from 'webapp/components/block/settings/metric/record-limit';
import BlockSettingsMetricShowNumberComponent from 'webapp/components/block/settings/metric/show-number';
import BlockSettingsMetricShowSmallChartComponent from 'webapp/components/block/settings/metric/show-small-chart';
import BlockSettingsMetricShownLabelsComponent from 'webapp/components/block/settings/metric/shown-labels';
import BlockSettingsMetricStackedComponent from 'webapp/components/block/settings/metric/stacked';
import BlockSettingsMetricTitleComponent from 'webapp/components/block/settings/metric/title';
import ColorBlockComponent from 'webapp/components/color-block';
import type { Option as ColorOption } from 'webapp/components/common/popups/popup-colors';
import CommonPopupsPopupColorsComponent from 'webapp/components/common/popups/popup-colors';
import CommonPopupsPopupListComponent from 'webapp/components/common/popups/popup-list';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormMetricSelectComponent from 'webapp/components/form/metric-select/index';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import type { ColorMappings } from 'webapp/components/form/select-attributes-with-colors';
import FormSelectAttributesWithColorsComponent from 'webapp/components/form/select-attributes-with-colors';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import RestrictedDropdownComponent from 'webapp/components/restricted-dropdown';
import RowErrorComponent from 'webapp/components/row-error';
import ToolTipComponent from 'webapp/components/tool-tip';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type DesignerSelectDimensionTour from 'webapp/models-custom/tour/designer/select-dimension';
import type VisualizationHorizontalBarChart from 'webapp/models-custom/visualization/horizontal-bar-chart';
import type VisualizationTable from 'webapp/models-custom/visualization/table';
import type DataSelectRecord from 'webapp/models/data-select';

interface Args {
	blockM: Block;
	model: DataSelect;
	openRecords: DataSelectRecord[];
	isSortable: boolean;
	toggle: () => void;
}

export default class BlockSettingsMetricComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('tour') declare tourService: Service.Tour;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.requestMetricOperatorLimitations();
	}

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get isOpen(): boolean {
		return this.args.openRecords.includes(this.args.model?.model);
	}

	get showAdditionalLines(): boolean {
		return this.visualization.showAdditionalLines;
	}

	get showCompares(): boolean {
		return this.visualization.showCompareOnPrimaryDs;
	}

	get selectedMetricData() {
		return this.args.model.metricData;
	}

	get showEmptySelectedMetrickeyError() {
		const selectedMetricData = this.selectedMetricData;
		return !this.args.model.hasNoMetricPermission && !selectedMetricData;
	}

	get showDimensions() {
		return this.args.model.metricSetup.allowDimensions;
	}

	get showFilters() {
		return (
			this.showAdvancedFilters || this.args.model.metricSetup.allowBasicDimensionValueFilters
		);
	}

	get showAdvancedFilters() {
		const ds = this.args.model;
		return ds.metricSetup.allowDimensions && ds.allowAdvancedFilters;
	}

	get allowMultipleDimensions() {
		const ds = this.args.model;
		return ds.metricSetup.allowDimensions && ds.allowAdvancedSetup;
	}

	get showInvalidFiltersWarning() {
		return !this.showFilters && !!this.args.model.metricSetup.dimensionFilterItemsCount;
	}

	get showInvalidDimensionsWarning() {
		return this.args.model.metricSetup.isDimensionSelectedButNotAllowed;
	}

	get showInvalidWarning() {
		return this.showInvalidDimensionsWarning || this.showInvalidFiltersWarning;
	}

	get showUnapplicableFiltersWarning() {
		// valid breakdown selected: only the filters that don't match it are unapplicable
		const ds = this.args.model;
		return ds.metricSetup.allowBasicDimensionValueFilters && ds.hasUnapplicableFilters;
	}

	get showFilterNotAppliedWarning() {
		return this.args.model.statusObjects.some((so) => so.isFilterNotApplied);
	}

	get selectionStatusObject() {
		const ds = this.args.model;
		return ds.isSourceDataset || ds.metricSetup.metric?.isTypeHorizon
			? ds.statusObjects.find((so) => so.isSelection)
			: undefined;
	}

	// Metric parity: while the Databoard filter isn't applied, the metric's own breakdown and filters stay hidden (as for
	// pre-built metrics); they could instead stay visible if we fall back to the block's previous filters.
	get showDimensionsSection() {
		return this.showDimensions && !this.showFilterNotAppliedWarning;
	}

	get showFiltersSection() {
		return this.showFilters && !this.showFilterNotAppliedWarning;
	}

	get isOutputMultipleAttributes() {
		return this.args.model.metricSetup.isOutputMultipleAttributes;
	}

	get maxAttributeLength() {
		return this.visualization.maxAttributeLength;
	}

	get requireSingleDimensionValue() {
		return (
			this.args.model.metricSetup.allowBasicDimensionValueFilters &&
			!this.visualization.allowMultipleMetricsOrDimensions
		);
	}

	get tooManyDimensionValuesSelectedErrorText() {
		if (this.requireSingleDimensionValue && this.isOutputMultipleAttributes) {
			return 'Visualization supports only one dimension value.';
		}
		return '';
	}

	get attributeError() {
		const errors: ValidationError[] = [];

		if (this.requireSingleDimensionValue) {
			if (
				!this.showAdvancedFilters &&
				!this.args.model.metricSetup.attributeNamesFromBasicFilters.length
			) {
				errors.push({ message: 'Must select one dimension value.' });
			}
			if (this.isOutputMultipleAttributes) {
				errors.push({ message: this.tooManyDimensionValuesSelectedErrorText });
			}
		}

		return errors;
	}

	get errorTextForFilters() {
		const dataSelect = this.args.model;
		if (dataSelect.allowAdvancedFilters && dataSelect.metricSetup.isFiltersInvalid) {
			return 'Invalid filter applied';
		} else if (this.tooManyDimensionValuesSelectedErrorText) {
			return this.tooManyDimensionValuesSelectedErrorText;
		}
		return '';
	}

	get hasUnits() {
		return this.args.model.metricSetup.allowUnitNameFilter;
	}

	get unitError() {
		const selectedUnit = this.args.model.metricSetup.unitName;
		const showError =
			!this.visualization.allowMultipleMetricsOrDimensions &&
			this.hasUnits &&
			(!selectedUnit || selectedUnit === Unit.ALL);

		return showError ? [{ message: 'Must select one unit.' }] : [];
	}

	get showRecordLimit(): boolean {
		return (
			this.visualization.allowMultipleMetricsOrDimensions &&
			this.args.model.isOutputUnlimitedMultiples
		);
	}

	get showStackedChoice(): boolean {
		return this.visualization.showStackedChoiceOnDs;
	}

	get showLabelsChoice(): boolean {
		return this.visualization.showLabelsChoiceOnDs;
	}

	get showAggregationChoice(): boolean {
		return this.visualization.showAggregationChoiceOnDs;
	}

	get showNumberChoice(): boolean {
		return this.visualization.canShowAndDisableNumberOnMetric;
	}

	get goalAlwaysVisible(): boolean {
		return this.visualization.goalAlwaysVisible;
	}

	get showMinValue(): boolean {
		return this.visualization.showMinValue;
	}

	get showMaxValue(): boolean {
		return this.visualization.showMaxValue;
	}

	get showSmallChartChoice(): boolean {
		return this.visualization.allowSmallChart;
	}

	get visualizationTypeIcon() {
		return this.mmBlocksService.getVisualizationTypeIcon(this.visualization.visualizationType);
	}

	get extraTitleLabel() {
		if (this.visualization.showSortDataSelect) {
			const visualization = this.visualization as
					VisualizationTable | VisualizationHorizontalBarChart,
				sortDataSelectId = visualization.sortDataSelect?.id;
			if (sortDataSelectId && this.args.model.id === sortDataSelectId) {
				return 'Main';
			}
		}
		return '';
	}

	@action
	changeTitle(title: string): void {
		const dataSelect = this.args.model;
		dataSelect.shownTitle = title;

		// Task: https://app.asana.com/0/0/1214518618113182/f
		const blockSetup = dataSelect.block?.blockSetup;
		if (blockSetup) {
			blockSetup.saveDataSelect(dataSelect);
		}
	}

	@action
	changeSelectedMetricData(metricData: DSSetup.MetricData): void {
		const dataSelect = this.args.model;
		this.blockSetup.changeMetric(dataSelect, metricData);
		this.blockSetup.trackDataSelectMetrickeyChangeEvent(dataSelect);
		if (this.blockSetup.dataSelectsShown.length === 1) {
			this.blockSetup.setTableColumnTypeBasedOnMetric();
		}
		this.requestMetricOperatorLimitations();
	}

	@action
	changeDimensions(dimensions: string[]): void {
		this.blockSetup.changeDimensions(this.args.model, dimensions);
		this.endDimensionsTour();
	}

	@action
	clearDimensionsAndFilters(): void {
		this.changeDimensions([]);
		this.blockSetup.changeFilters(this.args.model, null);
	}

	@action
	removeUnapplicableFilters(): void {
		const ds = this.args.model;
		this.blockSetup.changeFilters(ds, ds.metricSetup.filtersDataWithoutUnapplicableItems);
	}

	@action
	changeAttributes(attributes: string[] | null): void {
		this.blockSetup.changeAttributes(this.args.model, attributes || []);
	}

	@action
	changeUnit(unit: string | null): void {
		this.blockSetup.changeUnit(this.args.model, unit!);
	}

	@action
	onAfterMetricEdit() {
		this.blockSetup.validateDimensionsAfterMetricEdit(this.args.model);
	}

	@action
	openAdvanced(): void {
		// make a copy of block bcos of fresh blockSetup and data-select instances
		const block2 = this.mmBlocksService.createModelFromRecord(this.blockSetup.block.model),
			blockSetup = block2.blockSetup,
			dataSelect = blockSetup.dataSelects.find((ds) => ds.id === this.args.model.id);
		if (dataSelect) {
			blockSetup.isAutosaveOn = false; // in advanced, saves with button
			this.mmDataSelectsService.openAdvancedPopup({
				dataSelect,
				blockSetup, // if we passed blockSetup, we also show the secondaryDs, and use blockSetup update logic for all
				sourceForTracking: 'designer-link',
			});
		}
	}

	@action
	delete(): void {
		const onConfirm = () => {
			this.blockSetup.deleteDataSelect(this.args.model);
		};
		// no metricData = no confirm
		const selectedMetricData = this.selectedMetricData;
		if (selectedMetricData) {
			this.popupService.openConfirmPopup({
				onConfirm,
				title: `Are you sure you want to remove the metric "${this.args.model.shownTitle}"?`,
				text: '',
				confirmAction: 'Remove',
				cancelAction: 'Cancel',
			});
		} else {
			onConfirm();
		}
	}

	// ----- visualization (line/bar) stuff -----

	get selectedVisualization(): SeriesVisualization | null {
		if (!this.canChangeVisualization) {
			return null;
		}
		let visualization = this.args.model.visualization;
		if (!visualization) {
			// default for the given type of chart
			visualization = this.visualization.isBarChart
				? SeriesVisualization.BAR
				: SeriesVisualization.LINE;
		}
		return visualization || null;
	}

	get selectedVisualizationIcon() {
		return this.selectedVisualization === SeriesVisualization.BAR ? 'ChartBar' : 'ChartLine';
	}

	get canChangeVisualization(): boolean {
		return this.visualization.canChangeVisualizationForSpecificMetric;
	}

	get selectedVisualizationColor() {
		return this.selectedColorHex || 'var(--databox-neutral-400)'; // fallback to default color;
	}

	@action
	openVisualizationOptions(e: PointerEvent) {
		if (this.popupService.isOpen(CommonPopupsPopupListComponent)) {
			return;
		}

		e.stopPropagation(); // don't toggle the section

		if (!this.canChangeVisualization) {
			return;
		}

		const attachTo = e.target as HTMLElement;
		const popup = this.popupService.createListPopup(attachTo, Theme.DARK);

		if (popup) {
			const selectedVisualization = this.selectedVisualization;
			const itemsList: PopupListItem<''>[] = [
				{
					id: SeriesVisualization.LINE,
					text: 'Line',
					icon: 'ChartLine',
					action: '', // operating with id
					isSelected: selectedVisualization === SeriesVisualization.LINE,
				},
				{
					id: SeriesVisualization.BAR,
					text: 'Bar',
					icon: 'ChartBar',
					action: '', // operating with id
					isSelected: selectedVisualization === SeriesVisualization.BAR,
				},
			];
			popup.open({
				itemsList,
				doAction: (actionName: string, visualization: SeriesVisualization) =>
					this.changeVisualization(actionName, visualization),
			});
		}
	}

	@action
	changeVisualization(_actionName: string, visualization: SeriesVisualization) {
		const dataSelect = this.args.model;
		dataSelect.visualization = visualization;
		this.blockSetup.saveDataSelect(dataSelect);

		// bar / line can have different colors, but if we switch to the same we need to clear them
		this.blockSetup.checkBlockColorValidity();
	}

	@action
	requestMetricOperatorLimitations() {
		if (!this.showAdvancedFilters) {
			return;
		}
		const metricData = this.args.model.metricSetup.metricData;
		if (metricData) {
			// request operator limitations for each dimension
			const dimensions =
				this.args.model.metricSetup.dimensionFilterGroup?.filterItemsDimensions || [];
			dimensions.forEach((d) => {
				void this.mmMetricsService.getMetricOperatorLimitationsForDimension(
					[metricData],
					d,
				);
			});
		}
	}

	@cached
	get usedColors() {
		return this.blockSetup.getUsedColorsOnDataSelect(this.args.model);
	}

	@cached
	get colorOptions(): ColorOption[] {
		// returns color options with all the colors outside of this dataselect disabled

		let thisVisualizationIndex = 0; // matches the visualization.getDataSelectsByColorGroups index
		if (this.canChangeVisualization) {
			if (this.selectedVisualization === SeriesVisualization.BAR) {
				thisVisualizationIndex = 1;
			}
		}
		const usedColors = this.blockSetup.getUsedColors(thisVisualizationIndex).filter((c) => {
			return !this.usedColors.includes(c);
		});

		// copy, bcos we are changing the disabled option
		const options = copyObject(this.blockSetup.allColorOptions);
		options.forEach((o: ColorOption) => {
			o.disabled = usedColors.includes(o.id);
		});

		return options;
	}

	// ----- Metric color stuff -----

	get selectedColor() {
		return this.blockSetup.getColorForDataSettings(this.args.model.numericId!);
	}

	get selectedColorHex(): string {
		const selectedColor = this.selectedColor;
		if (typeof selectedColor === 'number') {
			return this.colorOptions.find((c) => c.id === selectedColor)?.hex || '';
		} else {
			return selectedColor || '';
		}
	}

	get canChangeColor(): boolean {
		return this.visualization.canChangeColorsForSpecificMetrics;
	}

	get colorOptionsForMetric(): ColorOption[] {
		// this is for the metric color picker
		// disable all used on attributes
		const usedOnAttributes = this.usedColors.filter((c) => c !== this.selectedColor);

		// copy, bcos we are changing the disabled option
		const options: ColorOption[] = copyObject(this.colorOptions);
		options.forEach((o) => {
			if (usedOnAttributes.includes(o.id)) {
				o.disabled = true;
			}
		});

		return options;
	}

	@action
	openColorOptions(e: PointerEvent) {
		const isPopupOpen = this.popupService.isOpen(CommonPopupsPopupColorsComponent);

		if (!isPopupOpen) {
			const attachTo = e.target as HTMLElement;

			const popup = this.popupService.createContext(
				CommonPopupsPopupColorsComponent,
				attachTo,
				{
					offset: 0,
					popupClass: 'popup-type-color popup-type-color--block',
					theme: 'dark',
					allowStacked: true,
				},
			);

			const selectedColor = this.selectedColor === null ? '' : this.selectedColor;
			popup.open({
				selected: selectedColor,
				allowEmpty: true,
				allowCustom: this.canChangeColor,
				emptyText: 'Auto',
				changeColor: this.changeColor,
				allColors: this.colorOptionsForMetric,
			});
		}
	}

	@action
	changeColor(color: number | string | null) {
		const dataSelectId = this.args.model.numericId;
		if (dataSelectId) {
			const normalizedColor = color === '' ? null : color;
			this.blockSetup.setColorForDataSettings(normalizedColor, dataSelectId);
		}
	}

	// ----- Attribute color stuff -----

	get canChangeColorOnAttributes(): boolean {
		return this.visualization.canChangeColorsForSpecificAttributes;
	}

	@cached
	get colorOptionsForAttributes(): ColorOption[] {
		// this is for the attributes color picker
		// disable the one used on current metric
		const usedOnMetric = this.selectedColor;

		// copy, bcos we are changing the disabled option
		const options: ColorOption[] = copyObject(this.colorOptions);
		options.forEach((o: ColorOption) => {
			if (usedOnMetric === o.id) {
				o.disabled = true;
			}
		});

		return options;
	}

	@cached
	get attributeColorMappings() {
		const dataSelectId = this.args.model.numericId;
		const mappings: ColorMappings = {};
		this.block.colors
			.filter((cs) => {
				return cs.dataSelectId === dataSelectId;
			})
			.forEach((cs) => {
				if (cs.dimension) {
					mappings[cs.dimension] = cs.color;
				}
			});
		return mappings;
	}

	@action
	changeAttributeColor(attribute: string, color: number | string | null) {
		const dataSelectId = this.args.model.numericId;
		if (dataSelectId) {
			const normalizedColor = color === '' ? null : color;
			this.blockSetup.setColorForDataSettings(normalizedColor, dataSelectId, attribute);
		}
	}

	// ----- dimensions tour blue bubble ----
	private tour: DesignerSelectDimensionTour | null = null;
	@action
	checkToStartDimensionsTour(): void {
		if (this.checkDimensionsTourStartConditions()) {
			this.startDimensionsTour();
		}
	}

	private checkDimensionsTourStartConditions(): boolean {
		return (
			isAlive(this) &&
			this.tourService.isEligibleForDesignerSelectDimensionTour &&
			!this.popupService.anyPopupsOpen &&
			this.isOpen &&
			this.args.model.isPrimaryMain
		);
	}

	private startDimensionsTour(): void {
		if (this.tour) {
			return;
		}

		const tour = this.tourService.initTour(TourId.DESIGNER__SELECT_DIMENSION);

		this.tour = tour;
		later(() => {
			// check again just to be sure
			if (this.checkDimensionsTourStartConditions()) {
				tour?.start();
			} else {
				this.endDimensionsTour();
			}
		}, 500);
	}

	@action
	endDimensionsTour(): void {
		if (this.tour) {
			this.tour.end();
			this.tour = null;
		}
	}
	<template>
		<div
			class="bs__metric {{if this.isOpen 'bs__metric--open'}} {{if @isSortable 'sortable'}}"
			data-id={{@model.id}}
			data-order={{@model.order}}
		>

			<BlockSettingsMetricTitleComponent
				@model={{@model}}
				@isOpen={{this.isOpen}}
				@toggle={{@toggle}}
				@changeTitle={{this.changeTitle}}
			>
				<CommonUiIconComponent @name="GripVertical" @size="sm" @class="sort-handle" />

				<div
					class="bs__metric__visualization
						{{if this.canChangeVisualization 'bs__metric__visualization--clickable'}}"
					role="button"
					{{on "click" this.openVisualizationOptions}}
				>
					{{#if this.selectedVisualization}}
						<CommonUiIconComponent
							@name={{this.selectedVisualizationIcon}}
							@fill={{this.selectedVisualizationColor}}
							@size="lg"
						/>
					{{else if this.visualizationTypeIcon}}
						<CommonUiIconComponent
							@name={{this.visualizationTypeIcon}}
							@fill={{this.selectedVisualizationColor}}
							@size="lg"
						/>
					{{/if}}
				</div>

				{{#if this.extraTitleLabel}}
					<span class="bs__metric__extra-title-label">{{this.extraTitleLabel}}</span>
				{{/if}}

				{{#if this.canChangeColor}}
					<a class="bs__metric__color-picker" {{on "click" this.openColorOptions}}>
						<ColorBlockComponent @color={{this.selectedColorHex}} />
					</a>
				{{/if}}

				<div class="bs__metric__toggler">
					<CommonUiIconComponent @name="ChevronDown" @size="sm" />
				</div>

			</BlockSettingsMetricTitleComponent>

			<div class="bs__metric__content">

				{{#if this.isOpen}}

					<div class="row {{if this.showEmptySelectedMetrickeyError 'errorClass'}}">
						{{#if @model.hasNoMetricPermission}}

							<RestrictedDropdownComponent @name="metrickey" />

						{{else}}

							<FormMetricSelectComponent
								@selected={{this.selectedMetricData}}
								@onChange={{this.changeSelectedMetricData}}
								@onAfterMetricEdit={{this.onAfterMetricEdit}}
								@origin="designer"
								@originForMetricFavorite="designer_dropdown"
								@placeholder="Select a metric"
								@theme="dark"
								@size="big"
							/>

						{{/if}}
					</div>

					{{#if @model.metricSetup.metric.isDeprecated}}
						<div class="bs__metric-deprecated-warning">
							<CommonUiIconComponent @name="AlertFill" @size="sm" />
							<p>
								The selected metric has been deprecated. You can remove it or select
								another metric.
							</p>
						</div>
					{{/if}}

					{{#if this.selectionStatusObject}}
						<div class="bs__dimension-warning">
							<CommonUiIconComponent @name="AlertFill" @size="sm" />
							<p>{{this.selectionStatusObject.description}}</p>
							<a
								class="button size-small"
								{{on "click" this.clearDimensionsAndFilters}}
							>
								OK, remove
							</a>
						</div>
					{{else if this.showInvalidWarning}}
						<div class="bs__dimension-warning">
							<CommonUiIconComponent @name="AlertFill" @size="sm" />
							<p>
								{{! datasets reject unsupported dimensions; pre-built metrics show their data without them }}
								{{#if @model.isSourceDataset}}
									The selected dimension(s) in the dashboard filter aren't
									supported for this metric, so there's no data to display. Remove
									them from this metric to see its overall data.
								{{else}}
									The selected dimension(s) in the dashboard filter aren't
									supported for this metric, so it shows the metric's data without
									them applied. Remove them from this metric to clear this
									warning.
								{{/if}}
							</p>
							<a
								class="button size-small"
								{{on "click" this.clearDimensionsAndFilters}}
							>
								OK, remove
							</a>
						</div>
					{{else if this.showUnapplicableFiltersWarning}}
						<div class="bs__dimension-warning">
							<CommonUiIconComponent @name="AlertFill" @size="sm" />
							<p>
								Some filters aren't supported for this metric, so it shows the
								metric's data without them applied. Remove them from this metric to
								clear this warning.
							</p>
							<a
								class="button size-small"
								{{on "click" this.removeUnapplicableFilters}}
							>
								OK, remove
							</a>
						</div>
					{{else if this.showFilterNotAppliedWarning}}
						<div class="bs__dimension-warning">
							<CommonUiIconComponent @name="AlertFill" @size="sm" />
							<p>
								The dashboard filter isn't supported for this metric, so it shows
								the metric's data without filters applied. Remove it from this
								metric to clear this warning.
							</p>
							{{! Metric parity: clears filters and breakdown; could instead fall back to the block's previous filters. }}
							<a
								class="button size-small"
								{{on "click" this.clearDimensionsAndFilters}}
							>
								OK, remove
							</a>
						</div>
					{{/if}}

					{{#if this.showDimensionsSection}}
						<div
							class="row bs__dimensions"
							{{didInsert this.checkToStartDimensionsTour}}
							{{didUpdate this.checkToStartDimensionsTour this.isOpen}}
							{{willDestroy this.endDimensionsTour}}
						>
							<label>Breakdown by</label>
							<FormSelectDimensionsComponent
								@metricSetup={{@model.metricSetup}}
								@onChange={{this.changeDimensions}}
								@isMultiple={{this.allowMultipleDimensions}}
								@showFloatlabel={{false}}
								@placeholder="Select dimension"
								@theme="dark"
							/>
						</div>
					{{/if}}

					{{#if this.showFiltersSection}}
						<div class="row row--filters">
							<label class="filter-label">
								<CommonUiIconComponent @name="Filter" @size="sm" />
								{{@model.metricSetup.dimensionFilterItemsCountText}}
							</label>

							{{#if this.showAdvancedFilters}}
								<BlockSettingsFiltersComponent
									@blockM={{@blockM}}
									@model={{@model}}
									@placeholder="Select"
									@theme="dark"
								/>
							{{else}}
								<span class="action-add action-add--disabled">
									<CommonUiIconComponent @name="Plus" @size="sm" />
									<ToolTipComponent
										@text="Advanced filtering is available for dataset metrics"
										@triggerFunc="click"
										@maxWidth={{230}}
									/>
								</span>
								{{#if this.canChangeColorOnAttributes}}
									<FormSelectAttributesWithColorsComponent
										@selected={{@model.metricSetup.attributeNamesFromBasicFilters}}
										@metricSetupForQuery={{@model.metricSetup}}
										@dateRangeForQuery={{@model.dateRangeCM}}
										@onChange={{this.changeAttributes}}
										@isMultiple={{true}}
										@showFloatlabel={{false}}
										@maxSelectionLength={{this.maxAttributeLength}}
										@theme="dark"
										@placeholder="Select"
										@colorOptions={{this.colorOptionsForAttributes}}
										@colorMappings={{this.attributeColorMappings}}
										@changeColor={{this.changeAttributeColor}}
									/>
								{{else}}
									<FormSelectAttributesComponent
										@selected={{@model.metricSetup.attributeNamesFromBasicFilters}}
										@metricSetupForQuery={{@model.metricSetup}}
										@dateRangeForQuery={{@model.dateRangeCM}}
										@onChange={{this.changeAttributes}}
										@isMultiple={{true}}
										@showFloatlabel={{false}}
										@placeholder="Select"
										@maxSelectionLength={{this.maxAttributeLength}}
										@theme="dark"
									/>
								{{/if}}
							{{/if}}
							<RowErrorComponent @items={{this.attributeError}} />
							{{#if this.errorTextForFilters}}
								<span class="error-message">{{this.errorTextForFilters}}</span>
							{{/if}}
						</div>
					{{/if}}

					{{#if this.hasUnits}}
						<div class="row">
							<label class="right--half">Unit</label>
							<div class="right--half">
								<FormSelectUnitsComponent
									@selected={{@model.metricSetup.unitName}}
									@metricSetupForQuery={{@model.metricSetup}}
									@dateRangeForQuery={{@model.dateRangeCM}}
									@onChange={{this.changeUnit}}
									@allowAllUnits={{this.visualization.allowMultipleMetricsOrDimensions}}
									@showFloatlabel={{false}}
									@isMultiple={{false}}
									@theme="dark"
								/>
								<RowErrorComponent @items={{this.unitError}} />
							</div>
						</div>
					{{/if}}

					{{#if this.showRecordLimit}}
						<BlockSettingsMetricRecordLimitComponent
							@model={{@model}}
							@blockSetup={{this.blockSetup}}
							@theme="dark"
						/>
					{{/if}}

					{{#if this.showAdditionalLines}}
						<BlockSettingsMetricAdditionalLinesComponent
							@blockM={{@blockM}}
							@model={{@model}}
						/>
					{{/if}}

					{{#if this.showCompares}}
						<BlockSettingsMetricComparesComponent
							@blockSetup={{this.blockSetup}}
							@dataSelect={{@model}}
							@theme="dark"
						/>
					{{/if}}

					{{#if this.showStackedChoice}}
						<BlockSettingsMetricStackedComponent
							@blockM={{@blockM}}
							@dataSelect={{@model}}
						/>
					{{/if}}

					{{#if this.showNumberChoice}}
						<BlockSettingsMetricShowNumberComponent
							@blockM={{@blockM}}
							@dataSelect={{@model}}
						/>
					{{/if}}

					{{#if this.showSmallChartChoice}}
						<BlockSettingsMetricShowSmallChartComponent
							@blockM={{@blockM}}
							@dataSelect={{@model}}
						/>
					{{/if}}

					{{#if this.showLabelsChoice}}
						<BlockSettingsMetricShownLabelsComponent
							@blockM={{@blockM}}
							@dataSelect={{@model}}
						/>
					{{/if}}

					{{#if this.showAggregationChoice}}
						<BlockSettingsMetricAggregationComponent
							@blockM={{@blockM}}
							@dataSelect={{@model}}
						/>
					{{/if}}

					{{#if this.goalAlwaysVisible}}
						<BlockSettingsMetricGaugeGoalComponent
							@blockM={{@blockM}}
							@model={{@model}}
							@theme="dark"
						/>
					{{/if}}

					{{#if this.showMinValue}}
						<BlockSettingsMetricMinValueComponent
							@blockM={{@blockM}}
							@model={{@model}}
						/>
					{{/if}}

					{{#if this.showMaxValue}}
						<BlockSettingsMetricMaxValueComponent
							@blockM={{@blockM}}
							@model={{@model}}
						/>
					{{/if}}

					<div class="bs__metric__actions">
						<a
							class="bs__metric__actions__action bs__metric__actions__action--settings"
							role="button"
							{{on "click" this.openAdvanced}}
						>
							<CommonUiIconComponent @name="Settings" @size="sm" />
							Advanced metric settings
						</a>
						<a
							class="bs__metric__actions__action bs__metric__actions__action--delete"
							role="button"
							{{on "click" this.delete}}
						>
							<CommonUiIconComponent @name="Trash" @size="sm" />
							Remove
						</a>
					</div>
				{{/if}}
			</div>

		</div>
	</template>
}
